import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Printer, Save } from 'lucide-react';

import { GenerationLoadingOverlay } from '../components/ui/GenerationLoadingOverlay';
import { RubricChromeBar } from '../studio/components/rubric/RubricChromeBar';
import { RubricConfigPanel } from '../studio/components/rubric/RubricConfigPanel';
import { RubricMatrixGrid } from '../studio/components/rubric/RubricMatrixGrid';
import type { RubricDraft } from '../studio/types/rubricDraft';
import {
  resolveRubricOas,
  resolveRubricSubjectLabels,
  resolveRubricSubjectTags,
  resolveRubricTargetLevels,
  rubricRequiresCurriculumOa,
} from '../studio/types/rubricDraft';
import {
  applyAiRubricToDraft,
  createEmptyRubricDraft,
} from '../studio/constants/rubricLevelPresets';
import {
  buildCourseDashboardPath,
  buildCourseRubricPath,
  buildPrintRubricPath,
} from '../studio/constants/courseStudioRoutes';
import type { RubricCreateNavigationState } from '../studio/lib/studioCreateNavigation';
import { getCourseProfileById } from '../studio/services/courseProfileStorage';
import {
  createRubricId,
  getRubric,
  saveRubric,
} from '../studio/services/rubricStorage';
import { generateRubricWithAi, mapGenerateRubricError } from '../studio/api/rubricApi';
import { hasCurriculumSubjectSupport } from '../studio/constants/subjectsWithCurriculum';
import { getSubjectNameByTag } from '../studio/constants/subjectCatalog';
import { EXAM_LEVEL_OPTIONS } from '../studio/constants/examAiParams';
import { resolveCourseSubjectTags } from '../studio/constants/courseProfileUi';

const RUBRIC_GENERATION_MESSAGES = [
  'Leyendo el perfil del curso…',
  'Alineando criterios con el objetivo…',
  'Redactando descriptores por nivel de logro…',
  'Afinando la matriz de evaluación…',
] as const;

export function RubricBuilderPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const wizardSeedAppliedRef = useRef(false);
  const { courseId = '', id: routeId } = useParams<{ courseId: string; id: string }>();
  const course = useMemo(() => getCourseProfileById(courseId), [courseId]);
  const isNew = routeId === 'nueva';

  const [draft, setDraft] = useState<RubricDraft | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showOverlay, setShowOverlay] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!course) return;
    if (isNew) {
      // Evita borrar el seed al hacer replace del location.state.
      if (wizardSeedAppliedRef.current) return;
      const next = createEmptyRubricDraft(createRubricId(), courseId);
      const state = location.state as RubricCreateNavigationState | null;
      const seed = state?.teacherPrompt?.trim();
      if (seed) {
        next.objective = seed;
        if (!next.title.trim()) {
          next.title = seed.length > 72 ? `${seed.slice(0, 69)}…` : seed;
        }
      }
      wizardSeedAppliedRef.current = true;
      setDraft(next);
      if (seed) {
        navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
      }
      return;
    }
    wizardSeedAppliedRef.current = false;
    const existing = getRubric(routeId ?? '');
    if (existing && existing.courseId === courseId) {
      setDraft({ ...existing });
      return;
    }
    setDraft(createEmptyRubricDraft(routeId || createRubricId(), courseId));
  }, [course, courseId, isNew, location.pathname, location.search, location.state, navigate, routeId]);

  const handleSave = useCallback(async () => {
    if (!draft || !course) return;
    const trimmedObjective = draft.objective.trim();
    if (!draft.title.trim()) {
      setError('Escribe un título para la evaluación.');
      return;
    }
    if (!trimmedObjective) {
      setError('Escribe el objetivo de aprendizaje.');
      return;
    }
    const courseExtraTags = resolveCourseSubjectTags(course).filter(
      (tag) => tag !== String(course.subjectTag || '').trim().toLowerCase(),
    );
    const selectedOas = resolveRubricOas(draft);
    const needsOa = rubricRequiresCurriculumOa(
      draft,
      course.subjectTag,
      hasCurriculumSubjectSupport,
      courseExtraTags,
    );
    if (needsOa && selectedOas.length === 0) {
      setError('Selecciona al menos un Objetivo de Aprendizaje (OA) del currículum.');
      return;
    }
    const subjectTags = resolveRubricSubjectTags(draft, course.subjectTag, courseExtraTags);
    if (
      (draft.curriculumMode === 'interdisciplinar' || draft.curriculumMode === 'multidisciplinar') &&
      subjectTags.length < 2
    ) {
      setError('En modo inter/multi el curso debe cubrir al menos dos asignaturas (o añade una extra).');
      return;
    }
    if (
      draft.evaluationType === 'diagnostica' &&
      resolveRubricTargetLevels(draft, course.level).length === 0
    ) {
      setError('En diagnóstica selecciona al menos un nivel curricular.');
      return;
    }

    setIsSaving(true);
    setError(null);
    try {
      const saved = saveRubric({
        ...draft,
        courseId,
        objective: trimmedObjective,
        title: draft.title.trim(),
        oas: selectedOas,
        oaCode: selectedOas[0]?.code,
        oaText: selectedOas[0]?.text,
      });
      setDraft(saved);
      setStatus('Rúbrica guardada.');
      if (isNew) {
        navigate(buildCourseRubricPath(courseId, saved.id), { replace: true });
      }
    } finally {
      setIsSaving(false);
    }
  }, [course, courseId, draft, isNew, navigate]);

  const handleGenerate = useCallback(async () => {
    if (!draft || !course) return;
    const trimmedObjective = draft.objective.trim();
    if (trimmedObjective.length < 8) {
      setError('Escribe un objetivo de aprendizaje más detallado antes de generar.');
      return;
    }
    const courseExtraTags = resolveCourseSubjectTags(course).filter(
      (tag) => tag !== String(course.subjectTag || '').trim().toLowerCase(),
    );
    const selectedOas = resolveRubricOas(draft);
    const needsOa = rubricRequiresCurriculumOa(
      draft,
      course.subjectTag,
      hasCurriculumSubjectSupport,
      courseExtraTags,
    );
    if (needsOa && selectedOas.length === 0) {
      setError('Selecciona al menos un Objetivo de Aprendizaje (OA) antes de generar la matriz.');
      return;
    }
    const subjectTags = resolveRubricSubjectTags(draft, course.subjectTag, courseExtraTags);
    if (
      (draft.curriculumMode === 'interdisciplinar' || draft.curriculumMode === 'multidisciplinar') &&
      subjectTags.length < 2
    ) {
      setError('En modo inter/multi el curso debe cubrir al menos dos asignaturas (o añade una extra).');
      return;
    }
    const targetLevels = resolveRubricTargetLevels(draft, course.level);
    if (draft.evaluationType === 'diagnostica' && targetLevels.length === 0) {
      setError('En diagnóstica selecciona al menos un nivel curricular antes de generar.');
      return;
    }

    setIsGenerating(true);
    setShowOverlay(true);
    setError(null);

    try {
      const targetLevelLabels = targetLevels.map(
        (id) => EXAM_LEVEL_OPTIONS.find((option) => option.id === id)?.label || id,
      );
      const { rubric, provider } = await generateRubricWithAi({
        title: draft.title,
        objective: trimmedObjective,
        evaluationType: draft.evaluationType ?? 'formativa',
        levels: draft.levels,
        levelCount: draft.levelCount,
        courseProfile: course,
        criterionCount: draft.criterionCount ?? 4,
        transversalCriterionCount: draft.transversalCriterionCount ?? 2,
        perRoleCriterionCount: draft.perRoleCriterionCount ?? 2,
        scope: draft.scope ?? 'individual',
        projectRoles: draft.scope === 'group' ? draft.projectRoles : [],
        curriculumTarget: draft.curriculumTarget ?? null,
        oaText: selectedOas.map((oa) => oa.text).join('\n'),
        oas: selectedOas,
        curriculumMode: draft.curriculumMode ?? 'mono',
        extraSubjectTags: draft.extraSubjectTags ?? [],
        subjectLabels: resolveRubricSubjectLabels(
          draft,
          course.subjectTag,
          getSubjectNameByTag,
          courseExtraTags,
        ),
        targetLevels,
        targetLevelLabels,
        estadoProyecto: draft.estadoProyecto ?? 'inicio',
        avanceDescripcion: draft.avanceDescripcion ?? '',
      });

      const next = applyAiRubricToDraft(
        {
          ...draft,
          oas: selectedOas,
          oaCode: selectedOas[0]?.code,
          oaText: selectedOas[0]?.text,
        },
        rubric,
      );
      const saved = saveRubric(next);
      setDraft(saved);
      setStatus(
        provider === 'scaffold'
          ? 'Matriz generada en modo local (Mistral no configurado).'
          : 'Matriz generada con IA.',
      );
      if (isNew) {
        navigate(buildCourseRubricPath(courseId, saved.id), { replace: true });
      }
    } catch (e) {
      const code = e instanceof Error ? e.message : 'generate_rubric_failed';
      setError(mapGenerateRubricError(code));
    } finally {
      setIsGenerating(false);
      setShowOverlay(false);
    }
  }, [course, courseId, draft, isNew, navigate]);

  if (!course) {
    return (
      <div className="studio-page w-full min-w-0">
        <p className="text-[var(--sala-ink-muted)]">Curso no encontrado.</p>
        <Link to="/studio" className="sala-btn-outline mt-4 inline-flex">
          Volver al Studio
        </Link>
      </div>
    );
  }

  if (!draft) return null;

  return (
    <div className="studio-page w-full min-w-0">
      <GenerationLoadingOverlay visible={showOverlay} messages={RUBRIC_GENERATION_MESSAGES} />

      <div className="flex min-h-[calc(100dvh-8rem)] flex-col py-2">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0">
            <Link
              to={buildCourseDashboardPath(courseId)}
              className="mb-1.5 inline-flex items-center gap-1 text-xs font-semibold text-[var(--sala-accent-neon)] hover:underline"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              Volver a {course.displayName}
            </Link>
            <h1 className="font-display text-xl font-extrabold sm:text-2xl">
              {isNew && !draft.hasMatrix ? 'Nueva rúbrica' : draft.title.trim() || 'Editar rúbrica'}
            </h1>
          </div>
          <div className="flex flex-wrap gap-2">
            {draft.hasMatrix && draft.criteria.length > 0 && !isNew ? (
              <Link
                to={buildPrintRubricPath(courseId, draft.id)}
                className="sala-btn-outline text-xs shadow-sm"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Printer className="mr-1.5 inline h-3.5 w-3.5" />
                Exportar PDF
              </Link>
            ) : null}
            <button
              type="button"
              className="sala-btn-outline text-xs shadow-sm"
              onClick={handleSave}
              disabled={isSaving || isGenerating}
            >
              <Save className="mr-1.5 inline h-3.5 w-3.5" />
              {isSaving ? 'Guardando…' : 'Guardar'}
            </button>
          </div>
        </div>

        <RubricChromeBar draft={draft} course={course} onChange={setDraft} />

        {status ? (
          <p className="mb-2 mt-2 text-sm text-[var(--sala-accent-neon)]">{status}</p>
        ) : null}
        {error ? <p className="mb-2 mt-2 text-sm text-red-400">{error}</p> : null}

        <div className="mt-3 grid min-h-0 flex-1 gap-5 lg:grid-cols-[minmax(18rem,22rem)_1fr]">
          <RubricConfigPanel
            draft={draft}
            course={course}
            isGenerating={isGenerating}
            onChange={setDraft}
            onGenerate={handleGenerate}
          />
          <RubricMatrixGrid
            draft={draft}
            course={course}
            onChange={setDraft}
            onStatus={setStatus}
            onError={setError}
          />
        </div>
      </div>
    </div>
  );
}

/** Legacy — redirige rutas antiguas del Escritorio al hub. */
export function LegacyEscritorioRubricRedirect() {
  return <Navigate to="/studio" replace />;
}
